import { __assign } from "tslib";
import { CONTROL, CONTROL_WITH_CONFIG, DEFAULT_SPLIT_STATE_SLICE, ERROR_SELECTOR_NO_SPLITSTATE } from './constants';
import { isMainClient, matching } from './utils';
import { initialStatus } from './reducer';
export var getStateSlice = function (sliceName) { return function (state) { return state[sliceName]; }; };
export var defaultGetSplitState = getStateSlice(DEFAULT_SPLIT_STATE_SLICE);
/**
 * This function extracts a treatment evaluation from the Split state. It returns the treatment string value.
 * If a treatment is not found, it returns the default value, which is `'control'` if not specified.
 * A treatment is not found if an invalid Split state is passed or if a `getTreatments` action has not been dispatched for the provided feature flag name and key.
 *
 * @param {ISplitState} splitState
 * @param {string} featureFlagName
 * @param {SplitIO.SplitKey} key
 * @param {string} defaultValue
 */
export function selectTreatmentValue(splitState, featureFlagName, key, defaultValue) {
    if (defaultValue === void 0) { defaultValue = CONTROL; }
    return selectTreatmentWithConfig(splitState, featureFlagName, key, { treatment: defaultValue, config: null }).treatment;
}
/**
 * This function extracts a treatment evaluation from the Split state. It returns a treatment object containing its value and configuration.
 * If a treatment is not found, it returns the default value, which is `{ treatment: 'control', configuration: null }` if not specified.
 * A treatment is not found if an invalid Split state is passed or if a `getTreatments` action has not been dispatched for the provided feature flag name and key.
 *
 * @param {ISplitState} splitState
 * @param {string} featureFlagName
 * @param {SplitIO.SplitKey} key
 * @param {SplitIO.TreatmentWithConfig} defaultValue
 */
export function selectTreatmentWithConfig(splitState, featureFlagName, key, defaultValue) {
    if (defaultValue === void 0) { defaultValue = CONTROL_WITH_CONFIG; }
    var splitTreatments = splitState && splitState.treatments ? splitState.treatments[featureFlagName] : console.error(ERROR_SELECTOR_NO_SPLITSTATE);
    var treatment = splitTreatments ?
        key ?
            splitTreatments[matching(key)] :
            Object.values(splitTreatments)[0] :
        undefined;
    return treatment ? treatment : defaultValue;
}
/**
 * This function extracts a treatment evaluation from the Split state. It returns an object that contains the treatment string value and the status properties of the client: `isReady`, `isReadyFromCache`, `hasTimedout`, and `isDestroyed`.
 * If a treatment is not found, it returns the default value, which is `'control'` if not specified.
 * A treatment is not found if an invalid Split state is passed or if a `getTreatments` action has not been dispatched for the provided feature flag name and key.
 *
 * @param {ISplitState} splitState
 * @param {string} featureFlagName
 * @param {SplitIO.SplitKey} key
 * @param {string} defaultValue
 */
export function selectTreatmentAndStatus(splitState, featureFlagName, key, defaultValue) {
    if (defaultValue === void 0) { defaultValue = CONTROL; }
    var result = selectTreatmentWithConfigAndStatus(splitState, featureFlagName, key, { treatment: defaultValue, config: null });
    result.treatment = result.treatment.treatment;
    return result;
}
/**
 * This function extracts a treatment evaluation from the Split state. It returns an object that contains the treatment object and the status properties of the client: `isReady`, `isReadyFromCache`, `hasTimedout`, and `isDestroyed`.
 * If a treatment is not found, it returns the default value as treatment, which is `{ treatment: 'control', configuration: null }` if not specified.
 * A treatment is not found if an invalid Split state is passed or if a `getTreatments` action has not been dispatched for the provided feature flag name and key.
 *
 * @param {ISplitState} splitState
 * @param {string} featureFlagName
 * @param {SplitIO.SplitKey} key
 * @param {SplitIO.TreatmentWithConfig} defaultValue
 */
export function selectTreatmentWithConfigAndStatus(splitState, featureFlagName, key, defaultValue) {
    if (defaultValue === void 0) { defaultValue = CONTROL_WITH_CONFIG; }
    var treatment = selectTreatmentWithConfig(splitState, featureFlagName, key, defaultValue);
    var status = selectStatus(splitState, key);
    return __assign(__assign({}, status), { treatment: treatment });
}
/**
 * Extracts an object with the status properties of the SDK client or manager from the Split state.
 *
 * @param {ISplitState} splitState
 * @param {SplitIO.SplitKey} key To use only on client-side. Ignored in server-side. If a key is provided and a client associated to that key has been used, the status of that client is returned.
 * If no key is provided, the status of the main client and manager is returned (the main client shares the status with the manager).
 *
 * @returns {IStatus} The status of the SDK client or manager.
 *
 * @see {@link https://help.split.io/hc/en-us/articles/360038851551-Redux-SDK#subscribe-to-events}
 */
export function selectStatus(splitState, key) {
    var status = splitState ?
        isMainClient(key) ?
            splitState :
            splitState.status && splitState.status[matching(key)] :
        console.error(ERROR_SELECTOR_NO_SPLITSTATE);
    return status ?
        { isReady: status.isReady, isReadyFromCache: status.isReadyFromCache, isTimedout: status.isTimedout, hasTimedout: status.hasTimedout, isDestroyed: status.isDestroyed, lastUpdate: status.lastUpdate } : __assign({}, initialStatus);
}
